import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { irisError } from '../errors';
import LoadingDots from './LoadingDots';
import ThemeToggle from './ThemeToggle';

const FONT = "'DM Sans', system-ui, sans-serif";
const BUCKET = 'resources';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fileIcon(url) {
  const ext = (url || '').split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return '📄';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return '🖼';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['zip', 'rar', '7z'].includes(ext)) return '📦';
  return '📎';
}

export default function Resources({ user, role, onSignOut, embedded, isDark, onToggleTheme }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const [filter, setFilter] = useState('all');

  const canEdit = role === 'editor';

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('resources')
      .select('*')
      .order('created_at', { ascending: false });
    if (err) setError(irisError('IRIS-300', err.message));
    else setResources(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function addLink(e) {
    e.preventDefault();
    if (!newTitle.trim() || !newUrl.trim()) return;
    setAddingLink(true);
    setError('');
    const { error: err } = await supabase.from('resources').insert({
      title: newTitle.trim(),
      url: newUrl.trim(),
      type: 'link',
      uploaded_by: user?.email || null,
    });
    setAddingLink(false);
    if (err) { setError(irisError('IRIS-301', err.message)); return; }
    setNewTitle('');
    setNewUrl('');
    load();
  }

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(`Uploading ${file.name}…`);
    setError('');

    const safeName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(safeName, file, { upsert: false });

    if (upErr) {
      setError(irisError('IRIS-302', upErr.message));
      setUploading(false);
      setUploadProgress('');
      return;
    }

    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(safeName);
    const publicUrl = urlData?.publicUrl || '';

    const { error: dbErr } = await supabase.from('resources').insert({
      title: file.name,
      url: publicUrl,
      type: 'file',
      uploaded_by: user?.email || null,
    });

    setUploading(false);
    setUploadProgress('');
    if (dbErr) { setError(irisError('IRIS-301', dbErr.message)); return; }
    e.target.value = '';
    load();
  }

  async function deleteResource(r) {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    setDeletingId(r.id);
    if (r.type === 'file' && r.url) {
      const parts = r.url.split(`/${BUCKET}/`);
      const path = parts[parts.length - 1];
      if (path) await supabase.storage.from(BUCKET).remove([path]);
    }
    await supabase.from('resources').delete().eq('id', r.id);
    setDeletingId(null);
    load();
  }

  const filtered = filter === 'all' ? resources : resources.filter(r => r.type === filter);
  const fileCount = resources.filter(r => r.type === 'file').length;
  const linkCount = resources.filter(r => r.type === 'link').length;

  return (
    <div style={embedded ? {} : S.page}>
      {/* Header — hidden when embedded in Admin */}
      {!embedded && (
        <div style={S.header}>
          <div>
            <h1 style={S.brand}>Iris Digital Consulting</h1>
            <p style={S.sub}>Resources</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
              {user?.email} · {canEdit ? 'Editor' : 'Viewer'}
            </span>
            {onToggleTheme && <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />}
            <button onClick={onSignOut} style={S.btnSecondary}>Sign out</button>
          </div>
        </div>
      )}

      <div style={embedded ? { padding: '8px 0' } : S.content}>
        {/* Add section — editor only */}
        {canEdit && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {/* Upload file */}
            <div style={S.card}>
              <p style={S.sectionLabel}>Upload file</p>
              <p style={S.hint}>PDFs, images, spreadsheets, docs — up to 50 MB.</p>
              <label style={S.uploadArea}>
                <input type="file" onChange={handleFileUpload} disabled={uploading}
                  style={{ display: 'none' }} />
                <span style={{ fontSize: 28, marginBottom: 8, display: 'block' }}>📁</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {uploading ? uploadProgress : 'Click to choose a file'}
                </span>
              </label>
            </div>

            {/* Add link */}
            <div style={S.card}>
              <p style={S.sectionLabel}>Add link</p>
              <p style={S.hint}>Live site URLs, shared drives, external resources.</p>
              <form onSubmit={addLink} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input style={S.input} placeholder="Title (e.g. Client's live site)"
                  value={newTitle} onChange={e => setNewTitle(e.target.value)} />
                <input style={S.input} placeholder="https://…"
                  value={newUrl} onChange={e => setNewUrl(e.target.value)} />
                <button type="submit" style={S.btnPrimary} disabled={addingLink || !newTitle.trim() || !newUrl.trim()}>
                  {addingLink ? 'Adding…' : 'Add Link'}
                </button>
              </form>
            </div>
          </div>
        )}

        {error && <p style={{ color: 'var(--danger)', fontSize: 13, margin: '0 0 16px 0' }}>{error}</p>}

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { key: 'all', label: `All (${resources.length})` },
            { key: 'file', label: `Files (${fileCount})` },
            { key: 'link', label: `Links (${linkCount})` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{
                ...S.btnSecondary, fontSize: 13, padding: '7px 16px',
                background: filter === f.key ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'var(--bg-btn)',
                color: filter === f.key ? 'var(--accent)' : 'var(--text-muted)',
                borderColor: filter === f.key ? 'color-mix(in srgb, var(--accent) 30%, transparent)' : 'var(--border)',
              }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Resource list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}><LoadingDots /></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <p style={{ fontSize: 15, color: 'var(--text-dim)', margin: '0 0 4px 0' }}>No resources yet</p>
            <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>
              {canEdit ? 'Upload a file or add a link above.' : 'Nothing has been shared yet.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(r => (
              <div key={r.id} style={{
                ...S.card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
                opacity: deletingId === r.id ? 0.4 : 1,
                transition: 'opacity 0.15s',
              }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>
                  {r.type === 'link' ? '🔗' : fileIcon(r.url)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '3px 0 0 0' }}>
                    {r.type === 'file' ? 'File' : 'Link'} · {formatDate(r.created_at)}
                    {r.uploaded_by ? ` · ${r.uploaded_by}` : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    style={{ ...S.btnSecondary, fontSize: 12, padding: '6px 14px', textDecoration: 'none', display: 'inline-block' }}>
                    {r.type === 'file' ? 'Download' : 'Open'}
                  </a>
                  {canEdit && (
                    <button onClick={() => deleteResource(r)} disabled={deletingId === r.id}
                      style={{ ...S.btnDanger, fontSize: 12, padding: '6px 14px' }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    color: 'var(--text)',
    fontFamily: FONT,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 32px',
    borderBottom: '1px solid var(--border-subtle)',
  },
  brand: {
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
    fontFamily: "'Syne', system-ui, sans-serif",
  },
  sub: {
    fontSize: 12,
    color: 'var(--text-dim)',
    margin: '2px 0 0 0',
  },
  content: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '32px 24px',
  },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 14,
    padding: 24,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-dim)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    margin: '0 0 6px 0',
  },
  hint: {
    fontSize: 12,
    color: 'var(--text-faint)',
    margin: '0 0 14px 0',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--border-strong)',
    background: 'var(--bg-input)',
    color: 'var(--text)',
    fontSize: 14,
    fontFamily: FONT,
    outline: 'none',
  },
  uploadArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 16px',
    borderRadius: 12,
    border: '2px dashed var(--border-medium)',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    background: 'var(--bg-card)',
    textAlign: 'center',
  },
  btnPrimary: {
    padding: '10px 20px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: FONT,
  },
  btnSecondary: {
    padding: '8px 14px',
    borderRadius: 10,
    border: '1px solid var(--border-medium)',
    background: 'var(--bg-btn)',
    color: 'var(--text)',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: FONT,
  },
  btnDanger: {
    padding: '8px 14px',
    borderRadius: 10,
    border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
    background: 'transparent',
    color: 'var(--danger)',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: FONT,
  },
};
